import React, { useState } from 'react';
import { Camera, X, Sparkles, Image as ImageIcon, RotateCcw, Zap, Loader2, Search, MessageSquareQuote, ShieldAlert } from 'lucide-react';
import { analyzeIssueForSearch } from '../services/geminiService';
// Corrected import path for useTranslation
import { useTranslation } from '../LanguageContext';
import { Language } from '../types';
import { toast } from 'sonner';

interface CustomerAISearchProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyResults: (category: string, keyword: string) => void;
}

export const CustomerAISearch: React.FC<CustomerAISearchProps> = ({ isOpen, onClose, onApplyResults }) => {
  const { language } = useTranslation();
  const [photo, setPhoto] = useState<string | null>(null);
  const [guideText, setGuideText] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const texts: Record<string, Record<Language, string>> = {
    title: { EN: 'AI Diagnostic Search', KH: 'ការស្វែងរកដោយរោគវិនិច្ឆ័យ AI', ZH: 'AI 诊断搜索', JA: 'AI 診断検索', KO: 'AI 진단 검색' },
    sub: { EN: 'Diagnose fault & find expert shops', KH: 'វិភាគបញ្ហា និងស្វែងរកហាងដែលមានជំនាញ', ZH: '诊断故障并寻找专家店铺', JA: '故障を诊断して専門ショップを探す', KO: '결함을 진단하고 전문가 상점을 찾으십시오' },
    snap_area: { EN: 'Snap or upload the broken area', KH: 'ថតរូប ឬផ្ទុកឡើងកន្លែងដែលខូច', ZH: '拍摄或上传损坏部位', JA: '故障箇所の撮影またはアップロード', KO: '고장 난 부위를 촬영하거나 업로드하십시오' },
    match_msg: { EN: 'AI matches you to specialists', KH: 'AI នឹងស្វែងរកអ្នកជំនាញដែលសមស្របសម្រាប់អ្នក', ZH: 'AI 为您匹配专家', JA: 'AI がスペシャリストとマッチング', KO: 'AI가 전문가와 매칭해 드립니다' },
    context: { EN: 'Symptoms & Context (Optional)', KH: 'រោគសញ្ញា និងព័ត៌មានបន្ថែម (ជម្រើស)', ZH: '症状与背景（可选）', JA: '症状と背景（任意）', KO: '증상 및 상황(선택 사항)' },
    placeholder: { EN: 'e.g. It makes a loud grinding noise...', KH: 'ឧទាហរណ៍៖ វាលឺសម្លេងកកិតខ្លាំង...', ZH: '例如：它发出巨大的磨损声...', JA: '例：大きなきしみ音がする...', KO: '예: 큰 갈리는 소리가 납니다...' },
    analyzing: { EN: 'Diagnosing Fault...', KH: 'កំពុងវិភាគបញ្ហា...', ZH: '正在诊断故障...', JA: '故障を诊断中...', KO: '결함 진단 중...' },
    match_btn: { EN: 'Match Specialist Shops', KH: 'ស្វែងរកហាងជំនាញដែលសមស្រប', ZH: '匹配专家店铺', JA: '専門ショップとマッチング', KO: '전문가 상점 매칭' },
    error: { EN: 'AI was unable to diagnose. Try a clearer photo.', KH: 'AI មិនអាចវិភាគបានទេ។ សូមព្យាយាមថតរូបឱ្យច្បាស់ជាងនេះ។', ZH: 'AI 无法诊断。请尝试拍一张更清晰的照片。', JA: 'AI で诊断できませんでした。より鮮明な写真を試してください。', KO: 'AI가 진단할 수 없습니다. 더 선명한 사진을 시도해 보세요.' }
  };

  if (!isOpen) return null;

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setPhoto(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleAnalyze = async () => {
    if (!photo) return;
    setIsAnalyzing(true);
    const result = await analyzeIssueForSearch(photo, language, guideText);
    if (result) {
      onApplyResults(result.category, result.keyword);
      onClose();
      toast.success(`Found specialists for ${result.category}!`);
    } else {
      toast.error(texts.error[language]);
    }
    setIsAnalyzing(false);
  };

  const handleReset = () => {
    setPhoto(null);
    setGuideText('');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
      <div className="bg-white rounded-[3rem] w-full max-w-xl shadow-2xl animate-in zoom-in duration-300 border border-slate-100 relative overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-white relative z-10 shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-violet-600 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-violet-200"><Zap size={24} fill="currentColor" /></div>
            <div>
              <h2 className="text-xl font-black text-slate-900 tracking-tight">{texts.title[language]}</h2>
              <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest">{texts.sub[language]}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-900 transition-all"><X size={20} /></button>
        </div>

        <div className="p-10 space-y-8 overflow-y-auto">
          {!photo ? (
            <div className="relative h-72 rounded-[2.5rem] bg-slate-50 border-2 border-dashed border-slate-200 flex flex-col items-center justify-center group hover:bg-slate-100 hover:border-violet-200 transition-all duration-300">
              <Camera size={48} className="text-slate-200 mb-4" />
              <p className="text-sm font-black text-slate-400 uppercase tracking-widest text-center px-12 leading-relaxed">{texts.snap_area[language]}</p>
              <div className="mt-4 flex items-center gap-2 text-[9px] font-black text-slate-300 uppercase tracking-widest"><ShieldAlert size={12} /> {texts.match_msg[language]}</div>
              <input type="file" accept="image/*" capture="environment" onChange={handlePhotoUpload} className="absolute inset-0 opacity-0 cursor-pointer" />
            </div>
          ) : (
            <div className="space-y-6 animate-in fade-in duration-500">
              <div className="relative h-64 rounded-[2.5rem] overflow-hidden group shadow-2xl border-4 border-white shrink-0">
                <img src={photo} className="w-full h-full object-cover" alt="Fault" />
                <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><button onClick={handleReset} className="w-14 h-14 rounded-2xl bg-white text-violet-600 flex items-center justify-center shadow-xl hover:scale-110 transition-transform"><RotateCcw size={24} /></button></div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2"><MessageSquareQuote size={14} className="text-violet-600" /> {texts.context[language]}</label>
                <textarea value={guideText} onChange={(e) => setGuideText(e.target.value)} placeholder={texts.placeholder[language]} className="w-full p-6 rounded-[2rem] bg-slate-50 font-bold text-slate-900 outline-none focus:ring-4 focus:ring-violet-50 transition-all h-28 resize-none shadow-inner" />
              </div>
              <button onClick={handleAnalyze} disabled={isAnalyzing} className="w-full py-6 bg-slate-900 text-white rounded-[1.8rem] font-black text-lg hover:bg-violet-600 transition-all shadow-2xl shadow-slate-900/20 flex justify-center items-center gap-4 disabled:opacity-50 transform active:scale-95">
                {isAnalyzing ? <><Loader2 size={24} className="animate-spin" /><span className="uppercase tracking-widest text-sm">{texts.analyzing[language]}</span></> : <><Sparkles size={24} fill="currentColor" /><span className="uppercase tracking-widest text-sm">{texts.match_btn[language]}</span></>}
              </button>
            </div>
          )}
        </div>
        <div className={`absolute bottom-0 left-0 h-1 bg-violet-600 transition-all duration-1000 ${isAnalyzing ? 'w-full animate-progress' : 'w-0'}`} />
      </div>
    </div>
  );
};